import api from 'libs/axios/api';
import { apiService } from 'libs/axios/services';
import { replaceParams } from 'utils/helpers';

export abstract class BaseService<T, TCreate = Partial<T>, TUpdate = Partial<T>> {
    abstract notFoundMessage: string;

    abstract getAll(params?: any): Promise<ApiResponse<ResponseBase<T[]>>>;
    abstract getById(id: string): Promise<T>;
    abstract create(data: TCreate): Promise<T>;
    abstract update(id: string, data: TUpdate): Promise<T>;
    abstract deleteAll(ids: string[]): Promise<T[]>;
    abstract deleteById(id: string): Promise<void>;
    abstract downloadCSV(params?: URLSearchParams): Promise<ApiResponse<any>>;

    getStaffOptions = async (
        request: IGetListUserReq,
    ): Promise<ApiResponse<ResponseBase<IUserData[]>>> => {
        return apiService.get<IGetListUserReq, ResponseBase<IUserData[]>>('/user/options', request);
    };

    fetchCompanyOptions = async (
        request: FilterRequest<string>,
    ): Promise<ApiResponse<ResponseBase<Company[]>>> => {
        return apiService.get<GetCompaniesRequest, ResponseBase<Company[]>>(
            api.companyOptions,
            request,
        );
    };

    fetchMajorLocationOptions = async (
        request: IMajorLocationOptionsRequest<string>,
    ): Promise<ApiResponse<ResponseBase<IMajorLocation[]>>> => {
        return apiService.get<FilterRequest<String>, ResponseBase<IMajorLocation[]>>(
            request.company_code
                ? `${api.majorLocationOptions}?company_code=${request.company_code}`
                : api.majorLocationOptions,
            request,
        );
    };

    fetchMiddleLocationOptions = async (
        request: IMiddleLocationOptionsRequest<string>,
    ): Promise<ApiResponse<ResponseBase<IMiddleLocation[]>>> => {
        if (!request.major_location_id) {
            return {
                success: false,
                error: '',
            };
        }

        return apiService.get<FilterRequest<String>, ResponseBase<IMiddleLocation[]>>(
            replaceParams(api.middleLocationOptions, {
                major_location_id: request.major_location_id,
            }),
            request,
        );
    };

    fetchMinorLocationOptions = async (
        request: IMinorLocationOptionsRequest<string>,
    ): Promise<ApiResponse<ResponseBase<IMinorLocation[]>>> => {
        if (!request.major_location_id || !request.middle_location_id) {
            return {
                success: false,
                error: '',
            };
        }

        return apiService.get<FilterRequest<String>, ResponseBase<IMinorLocation[]>>(
            replaceParams(api.minorLocationOptions, {
                major_location_id: request.major_location_id,
                middle_location_id: request.middle_location_id,
            }),
            request,
        );
    };

    fetchMajorContentOptions = async (
        request: IMajorContentOptionsRequest<string>,
    ): Promise<ApiResponse<ResponseBase<IMajorContent[]>>> => {
        return apiService.get<FilterRequest<String>, ResponseBase<IMajorContent[]>>(
            api.majorContentOptions,
            request,
        );
    };

    fetchMiddleContentOptions = async (
        request: IMiddleContentOptionsRequest<string>,
    ): Promise<ApiResponse<ResponseBase<IMiddleContent[]>>> => {
        if (!request.major_content_id) {
            return {
                success: false,
                error: '',
            };
        }

        return apiService.get<FilterRequest<String>, ResponseBase<IMiddleContent[]>>(
            replaceParams(api.middleContentOptions, { major_content_id: request.major_content_id }),
            request,
        );
    };

    fetchMinorContentOptions = async (
        request: IMinorContentOptionsRequest<string>,
    ): Promise<ApiResponse<ResponseBase<IMinorContent[]>>> => {
        if (!request.major_content_id || !request.middle_content_id) {
            return {
                success: false,
                error: '',
            };
        }

        return apiService.get<FilterRequest<String>, ResponseBase<IMinorContent[]>>(
            replaceParams(api.minorContentOptions, {
                major_content_id: request.major_content_id,
                middle_content_id: request.middle_content_id,
            }),
            request,
        );
    };
}
